import type { SceneLayoutDeployTypeCode, SkuResourceModulesState } from '../types';

/**
 * 模块键 / 默认勾选 / 锁定表。
 * **真相源是各插件 `scenes.ts`**（`*_SKU_*` / `*_DIRECT_MERGED_*` / `*_ORDER_*`）；
 * 本文件为 platform hook / 预览提供同步副本。修改后请跑 `check-alignment` 的 platform↔scenes 用例。
 */

/** SKU 资源配置步可见模块（顺序对齐各插件 `*_SKU_*`） */
const SKU_RESOURCE_MODULE_KEYS: Record<SceneLayoutDeployTypeCode, readonly string[]> = {
  VirtualMachine: ['systemSetting', 'resource', 'service', 'dataDisk', 'dns'],
  ContainerInstance: ['systemSetting', 'resource', 'startupCommandArgs', 'envVars', 'service', 'storage', 'dns'],
  ModelSubscription: ['modelProvider', 'other'],
  ModelInstance: ['runtimeConfig', 'modelStorage', 'gatewayConfig', 'kvOffload', 'nodeGroups', 'service'],
  TrainingCluster: ['nodeGroups', 'storage', 'envVars', 'initCommand'],
  AppInstance: ['runtimeConfig', 'appService', 'envVars', 'nodeGroups', 'healthCheck', 'service', 'storage'],
  ElasticBareMetal: ['systemSetting', 'resource', 'service', 'dns'],
  BareMetal: ['systemSetting', 'bareMetalNodes', 'service'],
};

/** 直建合并「资源配置」步可见模块（顺序对齐各插件 `*_DIRECT_MERGED_*`） */
const DIRECT_RESOURCE_MODULE_KEYS: Partial<Record<SceneLayoutDeployTypeCode, readonly string[]>> = {
  VirtualMachine: ['baseInfo', 'network', 'systemSetting', 'resource', 'service', 'dataDisk', 'dns'],
  ContainerInstance: ['baseInfo', 'network', 'systemSetting', 'resource', 'startupCommandArgs', 'envVars', 'service', 'storage', 'dns'],
  ElasticBareMetal: ['baseInfo', 'network', 'systemSetting', 'resource', 'service', 'dns'],
  BareMetal: ['baseInfo', 'systemSetting', 'network', 'bareMetalNodes', 'service'],
  ModelSubscription: ['baseInfo', 'modelProvider', 'other'],
  ModelInstance: ['baseInfo', 'runtimeConfig', 'modelStorage', 'storage', 'gatewayConfig', 'kvOffload', 'nodeGroups', 'service'],
  /** 训练集群直建可见块（复用 MI 壳，不含 storage；storage 仅 SKU/下单） */
  TrainingCluster: ['baseInfo', 'runtimeConfig', 'nodeGroups', 'envVars', 'initCommand'],
  AppInstance: ['baseInfo', 'runtimeConfig', 'appService', 'envVars', 'nodeGroups', 'healthCheck', 'service', 'storage'],
};

/**
 * 下单可见模块（对齐各插件 `*_ORDER_*` / order scenes）。
 * 多数 = 直建去掉 baseInfo；训练集群 = SKU 列表（含 storage、不含 runtimeConfig）。
 */
const ORDER_RESOURCE_MODULE_KEYS: Record<SceneLayoutDeployTypeCode, readonly string[]> = {
  VirtualMachine: ['network', 'systemSetting', 'resource', 'service', 'dataDisk', 'dns'],
  ContainerInstance: ['network', 'systemSetting', 'resource', 'startupCommandArgs', 'envVars', 'service', 'storage', 'dns'],
  ElasticBareMetal: ['network', 'systemSetting', 'resource', 'service', 'dns'],
  BareMetal: ['systemSetting', 'network', 'bareMetalNodes', 'service'],
  ModelSubscription: ['modelProvider', 'other'],
  ModelInstance: ['runtimeConfig', 'modelStorage', 'storage', 'gatewayConfig', 'kvOffload', 'nodeGroups', 'service'],
  TrainingCluster: ['nodeGroups', 'storage', 'envVars', 'initCommand'],
  AppInstance: ['runtimeConfig', 'appService', 'envVars', 'nodeGroups', 'healthCheck', 'service', 'storage'],
};

/** 直建合并步默认勾选且不可取消的模块（须 ⊆ DIRECT keys） */
const DIRECT_RESOURCE_LOCKED_MODULE_KEYS: Partial<Record<SceneLayoutDeployTypeCode, readonly string[]>> = {
  VirtualMachine: ['baseInfo', 'network', 'systemSetting', 'resource'],
  ContainerInstance: ['baseInfo', 'network', 'systemSetting', 'resource'],
  ElasticBareMetal: ['baseInfo', 'network', 'systemSetting', 'resource'],
  BareMetal: ['baseInfo', 'systemSetting', 'network', 'bareMetalNodes'],
  ModelSubscription: ['baseInfo', 'modelProvider', 'other'],
  ModelInstance: ['baseInfo', 'runtimeConfig', 'modelStorage', 'service', 'nodeGroups'],
  TrainingCluster: ['baseInfo', 'runtimeConfig', 'nodeGroups'],
  AppInstance: ['baseInfo', 'runtimeConfig', 'appService', 'nodeGroups'],
};

/** 直建合并步默认勾选状态（key 集 = DIRECT keys） */
const DIRECT_RESOURCE_DEFAULT_ENABLED: Partial<Record<SceneLayoutDeployTypeCode, SkuResourceModulesState>> = {
  VirtualMachine: {
    baseInfo: true,
    network: true,
    systemSetting: true,
    service: false,
    resource: true,
    dataDisk: false,
    dns: false,
  },
  ContainerInstance: {
    baseInfo: true,
    network: true,
    systemSetting: true,
    startupCommandArgs: false,
    envVars: false,
    service: false,
    resource: true,
    storage: false,
    dns: false,
  },
  ElasticBareMetal: {
    baseInfo: true,
    network: true,
    systemSetting: true,
    resource: true,
    service: false,
    dns: false,
  },
  BareMetal: {
    baseInfo: true,
    network: true,
    systemSetting: true,
    bareMetalNodes: true,
    service: false,
  },
  ModelSubscription: {
    baseInfo: true,
    modelProvider: true,
    other: true,
  },
  ModelInstance: {
    baseInfo: true,
    runtimeConfig: true,
    modelStorage: true,
    storage: false,
    kvOffload: false,
    nodeGroups: true,
    gatewayConfig: false,
    service: true,
  },
  TrainingCluster: {
    baseInfo: true,
    runtimeConfig: true,
    nodeGroups: true,
    envVars: false,
    initCommand: false,
  },
  AppInstance: {
    baseInfo: true,
    runtimeConfig: true,
    appService: true,
    envVars: false,
    service: false,
    healthCheck: false,
    nodeGroups: true,
    storage: false,
  },
};

/** SKU 创建默认勾选状态（PRE_PAID fixed；key 集 = SKU keys） */
const SKU_DEFAULT_ENABLED: Partial<Record<SceneLayoutDeployTypeCode, SkuResourceModulesState>> = {
  VirtualMachine: {
    systemSetting: true,
    resource: true,
    service: false,
    dataDisk: false,
    dns: false,
  },
  ContainerInstance: {
    systemSetting: true,
    resource: true,
    startupCommandArgs: false,
    envVars: false,
    service: false,
    storage: false,
    dns: false,
  },
  ElasticBareMetal: {
    systemSetting: true,
    resource: true,
    service: false,
    dns: false,
  },
  BareMetal: {
    systemSetting: true,
    bareMetalNodes: true,
    service: false,
  },
  ModelSubscription: {
    modelProvider: true,
    other: false,
  },
  ModelInstance: {
    runtimeConfig: true,
    modelStorage: true,
    kvOffload: false,
    nodeGroups: true,
    gatewayConfig: false,
    service: true,
  },
  TrainingCluster: {
    nodeGroups: true,
    storage: false,
    envVars: false,
    initCommand: false,
  },
  AppInstance: {
    runtimeConfig: true,
    appService: true,
    envVars: false,
    service: false,
    healthCheck: false,
    nodeGroups: true,
    storage: false,
  },
};

export function getSkuResourceModuleKeys(deployTypeCode: SceneLayoutDeployTypeCode, _paymentTiming?: string): readonly string[] {
  return SKU_RESOURCE_MODULE_KEYS[deployTypeCode] ?? [];
}

export function getDirectResourceModuleKeys(deployTypeCode: SceneLayoutDeployTypeCode): readonly string[] {
  return DIRECT_RESOURCE_MODULE_KEYS[deployTypeCode] ?? [];
}

export function getDirectResourceLockedModuleKeys(deployTypeCode: SceneLayoutDeployTypeCode): readonly string[] {
  return DIRECT_RESOURCE_LOCKED_MODULE_KEYS[deployTypeCode] ?? [];
}

/** 下单资源模块（显式表，可能与「直建去 baseInfo」不同，如 TrainingCluster） */
export function getOrderResourceModuleKeys(deployTypeCode: SceneLayoutDeployTypeCode): readonly string[] {
  return ORDER_RESOURCE_MODULE_KEYS[deployTypeCode] ?? [];
}

/**
 * 固定规格 SKU：锁定模块与直建相同（仅保留 SKU 资源配置步可见的 key，过滤 baseInfo 等）。
 * 由直建锁定集推导，避免两套表漂移。
 */
export function getFixedSkuLockedModuleKeys(deployTypeCode: SceneLayoutDeployTypeCode): readonly string[] {
  const skuKeys = new Set(getSkuResourceModuleKeys(deployTypeCode));
  return getDirectResourceLockedModuleKeys(deployTypeCode).filter((key) => skuKeys.has(key));
}

/**
 * 下单锁定模块（含后付费）：直建锁定（去掉 baseInfo）∪ 规格 preset 已勾选模块。
 * 仅保留下单可见模块 key。
 */
export function resolveOrderLockedModuleKeys(
  deployTypeCode: SceneLayoutDeployTypeCode,
  presetSkuResourceModules?: SkuResourceModulesState,
): readonly string[] {
  const orderKeys = new Set(getOrderResourceModuleKeys(deployTypeCode));
  const directLocked = getDirectResourceLockedModuleKeys(deployTypeCode).filter((key) => orderKeys.has(key));
  const fromPreset = Object.entries(presetSkuResourceModules || {})
    .filter(([key, enabled]) => enabled === true && orderKeys.has(key))
    .map(([key]) => key);
  return Array.from(new Set([...directLocked, ...fromPreset]));
}

/**
 * 模块勾选锁定：
 * - 直建：`DIRECT_RESOURCE_LOCKED`
 * - 规格创建/编辑固定规格：与直建相同模块（见 getFixedSkuLockedModuleKeys）
 * - 下单（后付费 / 先付费固定 / 先付费自定义）：直建锁定 ∪ 规格 preset 已勾选
 * - 规格创建/编辑：后付费与自定义规格不锁定；固定规格见上
 */
export function resolveLockedModuleKeys(
  deployTypeCode: SceneLayoutDeployTypeCode,
  options?: {
    skuType?: string;
    directCreateMode?: boolean;
    orderMode?: boolean;
    presetSkuResourceModules?: SkuResourceModulesState;
  },
): readonly string[] {
  if (options?.orderMode) {
    return resolveOrderLockedModuleKeys(deployTypeCode, options.presetSkuResourceModules);
  }
  if (options?.directCreateMode) {
    return getDirectResourceLockedModuleKeys(deployTypeCode);
  }
  if (options?.skuType === 'fixed') {
    return getFixedSkuLockedModuleKeys(deployTypeCode);
  }
  return [];
}

export function buildDefaultDirectResourceModules(deployTypeCode: SceneLayoutDeployTypeCode): SkuResourceModulesState {
  const defaults = DIRECT_RESOURCE_DEFAULT_ENABLED[deployTypeCode];
  if (!defaults) {
    return Object.fromEntries(getDirectResourceModuleKeys(deployTypeCode).map((key) => [key, true]));
  }
  return { ...defaults };
}

/** 预览 / 确认步：仅按勾选项生成 enabledModules */
export function buildPreviewEnabledModules(
  moduleKeys: readonly string[],
  skuResourceModules?: SkuResourceModulesState,
): SkuResourceModulesState {
  if (!skuResourceModules || typeof skuResourceModules !== 'object') {
    return Object.fromEntries(moduleKeys.map((key) => [key, true]));
  }
  return Object.fromEntries(moduleKeys.map((key) => [key, skuResourceModules?.[key] === true]));
}

/** SKU 模块默认勾选：POST_PAID 全 false；PRE_PAID fixed 使用默认配置；custom 全 false */
export function buildDefaultSkuResourceModules(
  deployTypeCode: SceneLayoutDeployTypeCode,
  skuType: string | undefined,
  paymentTiming?: string,
): SkuResourceModulesState {
  if (paymentTiming === 'POST_PAID' || skuType === 'custom') {
    return Object.fromEntries(getSkuResourceModuleKeys(deployTypeCode, paymentTiming).map((key) => [key, false]));
  }
  const defaults = SKU_DEFAULT_ENABLED[deployTypeCode];
  if (defaults) {
    return { ...defaults };
  }
  return Object.fromEntries(getSkuResourceModuleKeys(deployTypeCode, paymentTiming).map((key) => [key, true]));
}
